import React, { useEffect, useState, useContext, useRef } from "react";
import axiosInstance from "utils/axiosInstance";
import { AuthContext } from "context/AuthContext";
import { parse, format, formatISO } from "date-fns";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "assets/css/MiniCalendar.css";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

// React Table
import {
  createColumnHelper,
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

// Icons
import { FaInfoCircle, FaCheck } from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import { TbCalendarPlus } from "react-icons/tb";
import {
  Badge,
  Button,
  EmptyState,
  Pagination,
  SectionHeader,
  SelectorFecha,
  selectClassNames,
} from "components/ui";
import { GiBarbecue, GiSoccerBall } from "react-icons/gi";

// ===== REACT SELECT Imports =====
import Select, { SingleValue, ActionMeta } from "react-select";

// 1) Define your option shape
interface EventTypeOption {
  value: string;
  label: string;
}

// 2) List of available event types
const eventTypeOptions: EventTypeOption[] = [
  { value: "Fútbol", label: "Fútbol" },
  { value: "Peña", label: "Peña" },
];

// ============ STAGGER ANIMATION VARIANTS (asistentes) ============
const attendeeContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.025 } },
};
const attendeeItem = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.18 } },
};

const formatDate = (isoDate: string) => {
  return format(new Date(isoDate), "dd/MM/yy");
};

// ===================== TYPES =====================
interface Persona {
  id: number;
  nombre: string;
}

interface Asistencia {
  id: number; // ID del evento
  fecha: string; // Fecha del evento
  tipo: string;  // "Peña" or "Fútbol"
  cancelado: boolean;
  Asistencia: {
    PersonaId: number;
    presente: boolean;
    Persona: Persona;
  }[];
}

// ========== MODAL COMPONENT ==========
const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  inputValue,
  setInputValue,
  onSave,
  tipo,
  setTipo,
  confirmText = "Guardar",
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  inputValue: string;
  setInputValue: (value: string) => void;
  onSave: () => void;
  tipo: string;
  setTipo: (value: string) => void;
  confirmText?: string;
}) => {



  // Close modal on 'Esc' key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  // Handle calendar date pick
  // ***** React Select logic *****
  // Find which option object matches the parent state's "tipo"
  const selectedOption = eventTypeOptions.find((opt) => opt.value === tipo);

  // When user picks an option from the dropdown
  const handleTipoChange = (
    newValue: SingleValue<EventTypeOption>,
    _action: ActionMeta<EventTypeOption>
  ) => {
    if (newValue) {
      setTipo(newValue.value);
    } else {
      // We do NOT want user to remove the option, so we won't set empty
      // but if we did, we'd do: setTipo("");
      // We'll just keep the old value or revert to "Fútbol"
      setTipo("Fútbol");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        // Overlay: clicking it closes the modal
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 6, 23, 0.55)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          {/* Stop click propagation so clicks inside the box don't close */}
          <motion.div
            className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-navy-800"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
          >
        <div className="mb-5">
          <h3 className="text-[19px] font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          {description && (
            <p className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>

        {/* FECHA */}
        <div className="mb-4 w-full">
          <SelectorFecha value={inputValue} onChange={setInputValue} />
        </div>

        {/* TYPE SELECT (REACT SELECT) */}
        <div className="mb-4 text-left"> {/* ensure label + field left-aligned */}
          <label className="mb-1.5 block text-[13px] font-medium text-gray-900 dark:text-gray-200">
            Tipo de evento
          </label>
          <Select<EventTypeOption>
            unstyled
            value={selectedOption}
            isSearchable={false}
            onChange={handleTipoChange}
            options={eventTypeOptions}
            classNames={selectClassNames<EventTypeOption>()}
            // No isClearable => user cannot remove the option
            isClearable={false}
          />
        </div>

        {/* BOTONES DE ACCION */}
        <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-200/80 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSave}>
            {confirmText}
          </Button>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ========== MAIN COMPONENT ==========

const AsistenciaABM = () => {
  const [eventos, setEventos] = useState<Asistencia[]>([]);
  const [filteredEventos, setFilteredEventos] = useState<Asistencia[]>([]);

  // By default, new events will have "Fútbol" as their initial type
  const { isLoggedIn } = useContext(AuthContext);

  // For create/edit modal
  const [nuevoEvento, setNuevoEvento] = useState("");
  const [tipo, setTipo] = useState("Fútbol"); // (1) "Fútbol" is default
  const [editEvento, setEditEvento] = useState<string | null>(null);

  // Personas & expanded
  const [personas, setPersonas] = useState<Persona[]>([]);

  // Loading states
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [, setLoadingPersonas] = useState(false);

  // == For date-based search ==
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchCalendar, setShowSearchCalendar] = useState(false);

  // useRef to detect outside clicks on search calendar
  const searchCalendarRef = useRef<HTMLDivElement>(null);

  // React Table pagination
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 7,
  });

  // === Modal State ===
  const [isModalOpen, setIsModalOpen] = useState(false);

  // =========== FETCHING DATA ===========
  const fetchEventos = async () => {
    try {
      setLoadingEventos(true);
      const res = await axiosInstance.get("/eventos");
      // Sort events so the latest date appears first
      const sortedData = res.data.sort(
        (a: Asistencia, b: Asistencia) =>
          new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );

      setEventos(sortedData);
      setFilteredEventos(sortedData);
    } catch (error) {
      console.error("Error al obtener los eventos:", error);
    } finally {
      setLoadingEventos(false);
    }
  };

  const fetchPersonas = async () => {
    try {
      setLoadingPersonas(true);
      const res = await axiosInstance.get("/personas");
      setPersonas(res.data);
    } catch (error) {
      console.error("Error al obtener las personas:", error);
    } finally {
      setLoadingPersonas(false);
    }
  };

  useEffect(() => {
    fetchEventos();
    fetchPersonas();
  }, []);

  // =========== CRUD LOGIC =============

  // CREATE new event
  const crearEvento = async () => {
    if (!nuevoEvento.trim()) {
      toast.error("La fecha del evento es obligatoria.");
      return;
    }
    try {
      const parsedDate = parse(nuevoEvento, "dd/MM/yyyy", new Date());
      const formattedDate = formatISO(parsedDate, { representation: "complete" });
      await axiosInstance.post("/eventos", {
        fecha: formattedDate,
        tipo,
      });
      setNuevoEvento("");
      setIsModalOpen(false);
      fetchEventos();
    } catch (error) {
      console.error("Error al crear el evento:", error);
    }
  };

  // UPDATE existing event
  const actualizarEvento = async () => {
    if (!nuevoEvento.trim()) {
      toast.error("La fecha del evento es obligatoria.");
      return;
    }
    try {
      const parsedDate = parse(nuevoEvento, "dd/MM/yyyy", new Date());
      const formattedDate = formatISO(parsedDate, { representation: "complete" });

      await axiosInstance.put(`/eventos/${editEvento}`, {
        fecha: formattedDate,
        tipo,
      });
      setNuevoEvento("");
      setEditEvento(null);
      setIsModalOpen(false);
      fetchEventos();
    } catch (error) {
      console.error("Error al actualizar el evento:", error);
    }
  };

  // CANCEL / REACTIVATE
  const toggleCancelEvent = async (eventoId: number, cancelado: boolean) => {
    try {
      await axiosInstance.put(`/eventos/${eventoId}/cancelar`, {
        cancelado: !cancelado,
      });
      fetchEventos();
    } catch (error) {
      console.error("Error al cancelar/revertir el evento:", error);
    }
  };

  // ATTENDANCE
  const actualizarAsistencias = async (
    idEvento: number,
    asistencias: { PersonaId: number; presente: boolean }[]
  ) => {
    try {
      await axiosInstance.put(
        `/eventos/${idEvento}/actualizar-asistencias`,
        { asistencias }
      );
      toast.success("Asistencias guardadas con éxito.");
    } catch (error) {
      console.error("Error al actualizar asistencias:", error);
      toast.error("Hubo un error al guardar las asistencias.");
    }
  };

  // =========== EDIT EVENT HELPER ===========
  const handleEditEvento = (evento: Asistencia) => {
    setEditEvento(String(evento.id));
    // Convert existing date to dd/MM/yyyy
    const dateObj = new Date(evento.fecha);
    const dateStr = format(dateObj, "dd/MM/yyyy");
    setNuevoEvento(dateStr);
    setTipo(evento.tipo);
    setIsModalOpen(true);
  };

  // =========== SEARCH BY DATE ===========
  const handleSearchDateChange = (selectedDate: Date) => {
    // Format the selected date to dd/MM/yyyy
    const formattedSearchDate = format(selectedDate, "dd/MM/yyyy");
    setSearchTerm(formattedSearchDate);
    setShowSearchCalendar(false);

    // Filter events that match this exact date
    const filtered = eventos.filter((evento) => {
      const eventFormatted = format(new Date(evento.fecha), "dd/MM/yyyy");
      return eventFormatted === formattedSearchDate;
    });
    setFilteredEventos(filtered);

    // Reset to first page
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Clear filter (Ver Todos)
  const handleClearFilter = () => {
    setSearchTerm("");
    setFilteredEventos(eventos);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // =========== EXPANSION LOGIC ===========
  const [expandedEvento, setExpandedEvento] = useState<number | null>(null);
  const toggleExpandEvento = (id: number) => {
    setExpandedEvento(expandedEvento === id ? null : id);
  };

  // =========== REACT TABLE SETUP ===========
  const columnHelper = createColumnHelper<Asistencia>();
  const columns: ColumnDef<Asistencia, any>[] = [
    columnHelper.accessor("id", {
      header: "ID",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("fecha", {
      header: "Fecha",
      cell: (info) => info.getValue(),
    }),
  ];

  const table = useReactTable({
    data: filteredEventos,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Get current page
  const currentEvents = table.getRowModel().rows.map((row) => row.original);

  // ========== CLOSE SEARCH CALENDAR ON OUTSIDE CLICK ==========
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        searchCalendarRef.current &&
        !searchCalendarRef.current.contains(e.target as Node)
      ) {
        setShowSearchCalendar(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      {/* HEADER: CREATE EVENT & SEARCH DATE */}
      <SectionHeader
        title="Eventos"
        description="Partidos y peñas registrados. Abrí uno para ver o editar sus asistentes."
        actions={
          <>
        {/* Button: Create New Event */}
        {isLoggedIn && (
          <Button
            variant="primary"
            leftIcon={<TbCalendarPlus className="h-4 w-4" />}
            onClick={() => {
              setNuevoEvento("");
              setTipo("Fútbol"); // default "Fútbol"
              setEditEvento(null);
              setIsModalOpen(true);
            }}
          >
            Crear evento
          </Button>
        )}
        {/* Modal for CREATE or EDIT */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditEvento(null);
          }}
          title={editEvento ? "Editar Evento" : "Crear Evento"}
          description={
            editEvento
              ? "Modifica la fecha/tipo del evento seleccionado"
              : "Introduce la fecha y tipo del nuevo evento"
          }
          inputValue={nuevoEvento}
          setInputValue={setNuevoEvento}
          tipo={tipo}
          setTipo={setTipo}
          onSave={editEvento ? actualizarEvento : crearEvento}
          confirmText={editEvento ? "Guardar Cambios" : "Crear"}
        />

        {/* New: Search by Date (Calendar) + Ver Todos button */}
        <div className="relative" ref={searchCalendarRef}>
          <Button
            variant="secondary"
            onClick={() => setShowSearchCalendar((prev) => !prev)}
            rightIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M8 7V3m8 4V3m-9 4h10M5 21h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            {searchTerm || "Buscar por fecha"}
          </Button>

          {/* The calendar for searching */}
          {showSearchCalendar && (
            <div className="calendar_mobile absolute right-0 z-10 mt-2 rounded-card border border-gray-200/80 bg-white p-3 shadow-pop dark:border-white/10 dark:bg-navy-800">
              <Calendar onChange={handleSearchDateChange} locale="es-ES" />
            </div>
          )}
        </div>
          </>
        }
      />

      {/* TABLE OR LISTING */}
      {/* Show "Ver Todos" ONLY if a date is selected */}
      {searchTerm && (
        <div className="mb-4 flex items-center gap-2">
          <Badge tone="brand">{searchTerm}</Badge>
          <Button variant="ghost" size="sm" onClick={handleClearFilter}>
            Ver todos
          </Button>
        </div>
      )}
      {loadingEventos ? (
        /* Esqueletos con la forma real de las filas */
        <div>
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="mb-2.5 flex items-center justify-between gap-3 rounded-card border border-gray-200/70 bg-white p-4 dark:border-white/5 dark:bg-navy-900"
            >
              <div className="flex items-center gap-3">
                <div className="skeleton h-9 w-9 rounded-control" />
                <div className="space-y-2">
                  <div className="skeleton h-3.5 w-20 rounded-full" />
                  <div className="skeleton h-3 w-14 rounded-full" />
                </div>
              </div>
              <div className="skeleton h-6 w-24 rounded-full" />
            </div>
          ))}
        </div>
      ) : currentEvents.length === 0 ? (
        <EmptyState
          icon={<TbCalendarPlus />}
          title={
            searchTerm ? "No hay eventos en esa fecha" : "Todavía no hay eventos"
          }
          description={
            searchTerm
              ? "Probá con otra fecha o volvé a la lista completa."
              : isLoggedIn
              ? "Creá el primero para empezar a registrar asistencias."
              : "Cuando se cargue el primer evento vas a verlo acá."
          }
          action={
            searchTerm ? (
              <Button variant="secondary" size="sm" onClick={handleClearFilter}>
                Ver todos
              </Button>
            ) : isLoggedIn ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setNuevoEvento("");
                  setTipo("Fútbol");
                  setEditEvento(null);
                  setIsModalOpen(true);
                }}
              >
                Crear evento
              </Button>
            ) : undefined
          }
        />
      ) : (
        currentEvents.map((evento: Asistencia) => (
          <div
            key={evento.id}
            className={`mb-2.5 overflow-hidden rounded-card border bg-white transition-colors duration-150 dark:bg-navy-900 ${
              expandedEvento === evento.id
                ? "border-gray-300 dark:border-white/15"
                : "border-gray-200/80 hover:border-gray-300 dark:border-white/5 dark:hover:border-white/15"
            }`}
          >
            {/* Expand Header */}
            <div
              className="group flex cursor-pointer items-center justify-between gap-3 p-4 transition-colors hover:bg-gray-50/60 dark:hover:bg-white/5"
              onClick={() => toggleExpandEvento(evento.id)}
              aria-expanded={expandedEvento === evento.id}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-[17px] ${
                    evento.cancelado
                      ? "bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-gray-500"
                      : "bg-brand-50 text-brand-600 dark:bg-white/10 dark:text-brand-200"
                  }`}
                >
                  {evento.tipo === "Fútbol" ? <GiSoccerBall /> : <GiBarbecue />}
                </span>

                <div className="min-w-0">
                  <h4
                    className={`tabular text-[15px] font-semibold dark:text-white ${
                      evento.cancelado
                        ? "text-gray-600 line-through dark:text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {formatDate(evento.fecha)}
                  </h4>
                  <p className="text-[12px] text-gray-700 dark:text-gray-500">
                    {evento.tipo}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {evento.cancelado ? (
                  <Badge tone="danger" icon={<FaInfoCircle />}>
                    Cancelado
                  </Badge>
                ) : (
                  <Badge tone="neutral" className="hidden sm:inline-flex">
                    {(evento.Asistencia || []).filter((a) => a.presente).length}{" "}
                    asistentes
                  </Badge>
                )}

                <motion.span
                  animate={{ rotate: expandedEvento === evento.id ? 180 : 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex text-gray-600 transition-colors group-hover:text-gray-700 dark:text-gray-500"
                >
                  <MdKeyboardArrowDown className="h-[18px] w-[18px]" />
                </motion.span>
              </div>
            </div>

            {/* Expanded Content */}
            <AnimatePresence initial={false}>
              {expandedEvento === evento.id && (
                <motion.div
                  key="expand"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.2 },
                  }}
                  className="overflow-hidden"
                >
              <div>
                {/* Buttons row */}
                {isLoggedIn && (
                  <div className="flex items-center gap-2 border-t border-gray-200/80 px-4 py-3 dark:border-white/10">
                    {/* EDITAR EVENTO BUTTON */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvento(evento);
                      }}
                    >
                      Editar evento
                    </Button>

                    {/* CANCELAR/REACTIVAR EVENTO BUTTON */}
                    <Button
                      variant={evento.cancelado ? "success-soft" : "destructive-soft"}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCancelEvent(evento.id, evento.cancelado);
                      }}
                    >
                      {evento.cancelado ? "Reactivar evento" : "Cancelar evento"}
                    </Button>
                  </div>
                )}

                {/* ASISTENCIAS */}
                <div className={`px-4 pt-4 ${isLoggedIn ? "" : "border-t border-gray-200/80 dark:border-white/10"}`}>
                  <motion.ul
                    variants={attendeeContainer}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3"
                  >
                    {personas.map((persona) => {
                      const asistencias = evento.Asistencia || [];
                      const asistencia = asistencias.find(
                        (a) => a.PersonaId === persona.id
                      );
                      const presente = asistencia?.presente || false;
                      const editable = isLoggedIn && !evento.cancelado;

                      return (
                        <motion.li key={persona.id} variants={attendeeItem}>
                          <motion.button
                            type="button"
                            whileTap={editable ? { scale: 0.96 } : undefined}
                            disabled={!editable}
                            onClick={() => {
                              if (!editable) return;
                              const checked = !presente;
                              const found = asistencias.find((a) => a.PersonaId === persona.id);
                              if (found) {
                                found.presente = checked;
                              } else {
                                asistencias.push({
                                  PersonaId: persona.id,
                                  presente: checked,
                                  Persona: persona,
                                });
                              }
                              // Update main state (mantiene las referencias compartidas)
                              const updatedEventos = eventos.map((ev) =>
                                ev.id === evento.id ? { ...ev, Asistencia: asistencias } : ev
                              );
                              setEventos([...updatedEventos]);
                            }}
                            className={`flex w-full items-center gap-2 rounded-control border px-3 py-2 text-left text-[13px] font-semibold transition-colors duration-150 ${
                              presente
                                ? "border-brand-500 bg-brand-500 text-white"
                                : "border-gray-200/80 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700"
                            } ${editable ? "cursor-pointer" : "cursor-default opacity-80"}`}
                          >
                            <span
                              className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[6px] border transition-colors ${
                                presente
                                  ? "border-transparent bg-white/25 text-white"
                                  : "border-gray-300 text-transparent dark:border-white/30"
                              }`}
                            >
                              <FaCheck className="h-2.5 w-2.5" />
                            </span>
                            <span className="truncate">{persona.nombre}</span>
                          </motion.button>
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                  {isLoggedIn && (
                    <div className="mb-4 mt-4 flex justify-end">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={evento.cancelado}
                        onClick={() =>
                          actualizarAsistencias(evento.id, evento.Asistencia || [])
                        }
                      >
                        Guardar asistencias
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
          </div>
        ))
      )}

      {/* PAGINATION FROM REACT TABLE */}
      <Pagination
        className="mt-5 border-t border-gray-200/70 pt-5 dark:border-white/10"
        pageIndex={table.getState().pagination.pageIndex}
        pageCount={table.getPageCount()}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
      />
    </div>
  );
};

export default AsistenciaABM;
